import { createCipheriv, createDecipheriv, randomBytes } from 'node:crypto';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';

const PROTOCOL_VERSION = 1;

interface SessionKeys {
  c2sKey: Uint8Array;
  s2cKey: Uint8Array;
  c2sBase: Uint8Array;
  s2cBase: Uint8Array;
}

const enc = new TextEncoder();

export function deriveSessionKeys(pairingKey: Uint8Array, sessionSalt: Uint8Array): SessionKeys {
  const derive = (info: string, length: number): Uint8Array =>
    hkdf(sha256, pairingKey, sessionSalt, enc.encode(info), length);

  return {
    c2sKey: derive('zaparoo-c2s-v1', 32),
    s2cKey: derive('zaparoo-s2c-v1', 32),
    c2sBase: derive('zaparoo-c2s-nonce-v1', 12),
    s2cBase: derive('zaparoo-s2c-nonce-v1', 12),
  };
}

export function buildNonce(base: Uint8Array, counter: bigint): Uint8Array {
  const nonce = new Uint8Array(12);
  nonce.set(base);
  const view = new DataView(nonce.buffer, nonce.byteOffset, nonce.byteLength);
  const hi = Number((counter >> 32n) & 0xffffffffn);
  const lo = Number(counter & 0xffffffffn);
  view.setUint32(4, view.getUint32(4) ^ hi, false);
  view.setUint32(8, view.getUint32(8) ^ lo, false);
  return nonce;
}

export class EncryptedSession {
  private keys: SessionKeys;
  private sendCounter = 0n;
  private recvCounter = 0n;
  private firstFrameSent = false;
  readonly authToken: string;
  readonly sessionSalt: Uint8Array;
  private aad: Uint8Array;

  private constructor(authToken: string, sessionSalt: Uint8Array, keys: SessionKeys) {
    this.authToken = authToken;
    this.sessionSalt = sessionSalt;
    this.keys = keys;
    this.aad = new TextEncoder().encode(`${authToken}:ws`);
  }

  static create(authToken: string, pairingKey: Uint8Array): EncryptedSession {
    const sessionSalt = randomBytes(16);
    const keys = deriveSessionKeys(pairingKey, sessionSalt);
    return new EncryptedSession(authToken, sessionSalt, keys);
  }

  encrypt(plaintext: string): string {
    const nonce = buildNonce(this.keys.c2sBase, this.sendCounter);
    this.sendCounter++;

    const cipher = createCipheriv('aes-256-gcm', this.keys.c2sKey, nonce, {
      authTagLength: 16,
    });
    cipher.setAAD(this.aad);

    const encrypted = cipher.update(plaintext, 'utf8');
    const final = cipher.final();
    const tag = cipher.getAuthTag();

    const result = new Uint8Array(encrypted.length + final.length + tag.length);
    result.set(encrypted, 0);
    result.set(final, encrypted.length);
    result.set(tag, encrypted.length + final.length);

    return Buffer.from(result).toString('base64');
  }

  decrypt(ciphertext: string): string {
    const nonce = buildNonce(this.keys.s2cBase, this.recvCounter);
    this.recvCounter++;

    const data = Buffer.from(ciphertext, 'base64');
    if (data.length < 16) {
      throw new Error('Ciphertext too short');
    }

    const encrypted = data.subarray(0, data.length - 16);
    const tag = data.subarray(data.length - 16);

    const decipher = createDecipheriv('aes-256-gcm', this.keys.s2cKey, nonce, {
      authTagLength: 16,
    });
    decipher.setAAD(this.aad);
    decipher.setAuthTag(tag);

    const decrypted = decipher.update(encrypted);
    const final = decipher.final();

    return Buffer.concat([decrypted, final]).toString('utf8');
  }

  encryptAndFrame(plaintext: string): string {
    const encrypted = this.encrypt(plaintext);
    if (!this.firstFrameSent) {
      this.firstFrameSent = true;
      return JSON.stringify({
        v: PROTOCOL_VERSION,
        e: encrypted,
        t: this.authToken,
        s: Buffer.from(this.sessionSalt).toString('base64'),
      });
    }
    return JSON.stringify({ e: encrypted });
  }
}
